import React, { useCallback, useEffect, useState } from 'react';

import { useApolloClient } from '@apollo/client';
import { parseInitialURLAsync } from 'expo-linking';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { useIntl } from 'react-intl';

import LoadingAnimation from 'components/loading-animation';
import LoadingContainer from 'components/loading-animation/loading-container';
import { toaster } from 'components/toaster/toaster';
import { useNavigation } from 'hooks/navigation/use-navigation';
import useEffectOnce from 'hooks/use-effect-once';
import { useDismissAllAndNavigate } from 'navigation/hooks/use-dismiss-and-navigate';
import { useAuthContext } from 'state/auth';
import { RBIPlatform, platform } from 'utils/environment';
import logger from 'utils/logger';
import { decodeBase64String, parseStringifiedJSON } from 'utils/parse-string';
import { routes } from 'utils/routing';
import { stringToBool } from 'utils/string-To-Bool';

import MissingLink from './missing-link';

export interface IValidateEmailForm {
  email: string;
}

const AuthChallengeScreen = () => {
  const {
    user: authUser,
    signOut,
    validateLogin,
    originLocation,
    setOriginLocation,
  } = useAuthContext();
  const { formatMessage } = useIntl();
  const client = useApolloClient();
  const { navigate } = useNavigation();
  const dismissAndNavigate = useDismissAllAndNavigate();
  const router = useRouter();
  const params = useLocalSearchParams<{ user: string; token: string; showToast?: string }>();
  const [isLoading, setIsLoading] = useState(true);
  const user = params.user || '';
  let jwt = params.token || '';
  const showToast = stringToBool(params.showToast);
  let username = decodeBase64String({ value: user }) || '';

  // Either if we have a login, or once we validate OTP and get an update for the user being loaded
  // we can then handle the navigate.
  useEffect(() => {
    if (authUser) {
      if (originLocation) {
        dismissAndNavigate(originLocation);
        setOriginLocation(null);
      } else {
        router.dismissAll();
      }

      if (showToast) {
        toaster.positive(formatMessage({ id: 'autoSignInSuccess' }));
      }
    }
  }, [
    authUser,
    dismissAndNavigate,
    formatMessage,
    navigate,
    originLocation,
    router,
    setOriginLocation,
    showToast,
  ]);

  const login = useCallback(
    async (email: any) => {
      setIsLoading(true);
      try {
        await validateLogin({ username: email, jwt });
        // refetch offers when user navigates to /offers page
        client.reFetchObservableQueries();
      } catch {
        setIsLoading(false);
        navigate(routes.base, { replace: true });
      }
    },
    [validateLogin, jwt, client, navigate]
  );

  const handleLogin = (jwtBody: string) => {
    const currentPlatform = platform();
    const originalRequestPlatform: RBIPlatform | undefined = parseStringifiedJSON({
      value: decodeBase64String({ value: jwtBody }) || '',
      defaultValue: {},
      onError: (error: Error) =>
        logger.warn({ error, message: 'Could not parse platform from auth jwt' }),
    }).platform;

    if (currentPlatform !== RBIPlatform.APP && originalRequestPlatform === RBIPlatform.APP) {
      setIsLoading(false);
      return;
    }

    login(username);
  };

  const handleDeepLink = async () => {
    try {
      // Parse the deep link that was used to launch the app
      const { queryParams } = await parseInitialURLAsync();

      if (!queryParams?.token || !queryParams.user) {
        signOut();
        setIsLoading(false);
        return;
      }

      username = decodeBase64String({ value: queryParams.user as string }) || '';
      jwt = queryParams.token as string;

      const jwtBody = jwt.split('.')[1];
      handleLogin(jwtBody);
    } catch (err) {
      signOut();
      setIsLoading(false);
    }
  };

  useEffectOnce(() => {
    if (!jwt) {
      handleDeepLink();
      return;
    }

    const jwtBody = jwt.split('.')[1];
    handleLogin(jwtBody);
  });

  if (isLoading) {
    return (
      <LoadingContainer>
        <LoadingAnimation />
      </LoadingContainer>
    );
  }

  return <MissingLink username={username} />;
};

export default AuthChallengeScreen;
